"use client";

import { useMemo, useState } from "react";
import { Check, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { RichContent } from "@/components/rich-content";
import { formatNumber } from "@/lib/format-datetime";
import { cn } from "@/lib/utils";
import { QuizRichText } from "@/features/lessons/components/QuizRichText";
import type { LearnCheckpoint as LearnCheckpointData } from "./api";
import { seededShuffle } from "./seeded-order";

/**
 * The inline checkpoint (`LN-RDR01`, docs/35 §5 item 4, `STYLE_GUIDE.md` §3
 * block 11): three questions the reader answers where they are reading, with the
 * feedback on a wrong option naming the `h2` to go back to.
 *
 * **It is not a quiz and it is deliberately not scored.** Nothing here is sent
 * to the quiz engine, no attempt is created and no mark is kept. The module test
 * is the graded thing and it is its own lesson. What this writes is one event,
 * once, when the reader has answered all three: enough for "you did this" and
 * nothing that could be read later as a result.
 *
 * **Options are rich text** (`LN-RDR02`). An option or its feedback is authored
 * HTML (`<code>return 0;</code>`), and until 2026-09-23 it was printed as a
 * string, so the reader saw the tags. They go through `QuizRichText` now, the
 * same renderer the course quiz uses, and the section carries
 * `learn-checkpoint` so the lesson body's list styles (a decimal `ol`, a disc
 * `ul`) stop numbering the questions a second time.
 *
 * Answering is a per-question decision the reader can change until they get it
 * right; the answer key arrives with the payload, because there is no mark to
 * protect and hiding it would cost a round trip per click on a page whose whole
 * point is reading without waiting.
 *
 * **Options are shuffled, the same way every time** (`LN-WEB02`). Authors put
 * the correct option first (296 of the C track's 300 questions), so the file
 * order gave the answer away. Each question's options are drawn in an order
 * seeded by the lesson and the question (`seeded-order.ts`), so the server and
 * the browser agree and a reload does not move them. Everything is keyed by the
 * option id, so the letter, the mark and the feedback follow their option. A
 * true or false question is shuffled too: the authors wrote those answer
 * first as well ("False | True" when False is right).
 */
export function LearnCheckpoint({
  checkpoints: authored,
  seed,
  contentLang,
  completed,
  onComplete,
}: {
  checkpoints: LearnCheckpointData[];
  /** What the option order is drawn from with each question id: the lesson id. */
  seed: string;
  /** Which language the reader is reading the body in, so the two agree. */
  contentLang: "en" | "bn";
  /** Whether this reader has already finished it, from the event ledger. */
  completed: boolean;
  onComplete: () => void;
}) {
  const t = useTranslations("learn.reader");
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [reported, setReported] = useState(completed);
  const checkpoints = useMemo(
    () =>
      authored.map((question) => ({
        ...question,
        options: seededShuffle(question.options, `${seed}:${question.id}`),
      })),
    [authored, seed],
  );

  if (checkpoints.length === 0) return null;

  const answeredCorrectly = checkpoints.filter((q) => {
    const choice = picked[q.id];
    return choice !== undefined && q.options.find((o) => o.id === choice)?.isCorrect === true;
  }).length;

  function pick(questionId: string, optionId: string, isCorrect: boolean): void {
    const next = { ...picked, [questionId]: optionId };
    setPicked(next);
    if (!isCorrect || reported) return;
    const done = checkpoints.every((q) => {
      const choice = next[q.id];
      return choice !== undefined && q.options.find((o) => o.id === choice)?.isCorrect === true;
    });
    if (done) {
      setReported(true);
      onComplete();
    }
  }

  const n = (value: number) => formatNumber(value, contentLang);

  return (
    <section
      aria-labelledby="learn-checkpoint"
      className="learn-checkpoint my-10 rounded-[var(--radius)] border border-[rgb(var(--border))] bg-[rgb(var(--surface))] p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2
          className="!m-0 font-[family-name:var(--font-heading)] text-xl font-bold text-[rgb(var(--foreground))]"
          id="learn-checkpoint"
        >
          {t("checkpointHeading")}
        </h2>
        <p className="!m-0 flex items-center gap-2.5 text-[13px] font-semibold text-neutral-600">
          <span aria-hidden className="flex gap-1">
            {checkpoints.map((question, index) => (
              <span
                className={cn(
                  "h-1.5 w-4 rounded-full",
                  index < answeredCorrectly ? "bg-emerald-500" : "bg-neutral-200",
                )}
                key={question.id}
              />
            ))}
          </span>
          {t("checkpointProgress", { done: n(answeredCorrectly), total: n(checkpoints.length) })}
        </p>
      </div>
      <p className="!mb-0 !mt-1.5 text-sm text-[rgb(var(--muted-foreground))]">
        {t("checkpointIntro")}
      </p>

      <ol className="!mt-5 space-y-6">
        {checkpoints.map((question, index) => {
          const choice = picked[question.id];
          const chosen = question.options.find((o) => o.id === choice);
          const questionHtml =
            contentLang === "bn" && question.questionText_bn
              ? question.questionText_bn
              : question.questionText_en;
          const feedback = chosen && !chosen.isCorrect
            ? (contentLang === "bn" && chosen.explanation_bn
                ? chosen.explanation_bn
                : chosen.explanation_en)
            : null;

          return (
            <li key={question.id}>
              <div className="flex gap-2.5">
                <span className="mt-px font-[family-name:var(--font-heading)] text-[15px] font-bold text-primary-ink">
                  {n(index + 1)}
                </span>
                <RichContent
                  className="min-w-0 flex-1 text-[15px] font-semibold [&_p]:!mb-0"
                  html={questionHtml}
                />
              </div>

              <ul className="!mt-3 space-y-2">
                {question.options.map((option, optionIndex) => {
                  const isChosen = choice === option.id;
                  const text =
                    contentLang === "bn" && option.text_bn ? option.text_bn : option.text_en;
                  const state = isChosen ? (option.isCorrect ? "right" : "wrong") : null;
                  return (
                    <li key={option.id}>
                      <Button
                        aria-pressed={isChosen}
                        className={cn(
                          // An option row is a choice, not a call to action, so
                          // it is the quiet variant with a token border rather
                          // than a fourth Button variant (`AGENTS.md` rule 17).
                          "h-auto w-full justify-start gap-3 whitespace-normal border px-3.5 py-2.5 text-left text-sm font-normal text-[rgb(var(--foreground))]",
                          state === "right"
                            ? "border-emerald-300 bg-emerald-50 hover:bg-emerald-50"
                            : state === "wrong"
                              ? "border-red-300 bg-red-50 hover:bg-red-50"
                              : "border-[rgb(var(--border))] bg-[rgb(var(--surface))]",
                        )}
                        onClick={() => pick(question.id, option.id, option.isCorrect)}
                        type="button"
                        variant="tertiary"
                      >
                        <span
                          aria-hidden
                          className={cn(
                            "flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-[family-name:var(--font-heading)] text-xs font-bold",
                            state === "right"
                              ? "bg-emerald-500 text-white"
                              : state === "wrong"
                                ? "bg-red-500 text-white"
                                : "bg-neutral-100 text-neutral-600",
                          )}
                        >
                          {"ABCDEFGH"[optionIndex] ?? optionIndex + 1}
                        </span>
                        <QuizRichText
                          as="span"
                          className="min-w-0 flex-1 [&_p]:!m-0"
                          html={text}
                        />
                        {state === "right" ? (
                          <span className="ml-auto flex shrink-0 items-center gap-1 text-xs font-bold text-emerald-700">
                            <Check aria-hidden className="h-3.5 w-3.5" strokeWidth={3} />
                            {t("checkpointRight")}
                          </span>
                        ) : state === "wrong" ? (
                          <X aria-hidden className="ml-auto h-4 w-4 shrink-0 text-red-700" strokeWidth={2.6} />
                        ) : null}
                      </Button>
                    </li>
                  );
                })}
              </ul>

              {chosen && !chosen.isCorrect ? (
                <div className="mt-2.5 rounded-[var(--radius)] border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900">
                  {feedback ? (
                    <QuizRichText className="[&_p]:!m-0" html={feedback} />
                  ) : (
                    t("checkpointTryAgain")
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>

      {reported ? (
        <p className="!mb-0 mt-6 flex items-center gap-2 text-sm font-semibold text-emerald-700">
          <Check aria-hidden className="h-4 w-4" />
          {t("checkpointDone")}
        </p>
      ) : null}
    </section>
  );
}
